import React, { useEffect, useState } from 'react'
import { X } from '@phosphor-icons/react'
import { useLocale } from 'src/lib/hooks/useLocal'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  IconButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea
} from '@shopflo/ui'

export interface CancellationReasonFormData {
  selectedOption: string
  detailedReason: string
}

const CANCELLATION_REASON_KEYS = [
  'cancellation_reason_not_useful',
  'cancellation_reason_hard_to_use',
  'cancellation_reason_expensive',
  'cancellation_reason_lack_of_support',
  'cancellation_reason_performance_issues',
  'cancellation_reason_better_alternative'
]

export const defaultCancellationReasonFormData: CancellationReasonFormData = {
  selectedOption: '',
  detailedReason: ''
}

interface CancellationReasonDialogProps {
  isOpen: boolean
  subtitle: string
  title: string
  isLoading?: boolean
  onClose: () => void
  onConfirm: (formData: CancellationReasonFormData) => void | Promise<void>
}

const CancellationReasonDialog: React.FC<CancellationReasonDialogProps> = ({
  isOpen,
  subtitle,
  title,
  isLoading = false,
  onClose,
  onConfirm
}) => {
  const { t } = useLocale()
  const [formData, setFormData] = useState(defaultCancellationReasonFormData)
  const [isReasonOpen, setIsReasonOpen] = useState(false)
  const hasSelectedReason = Boolean(formData.selectedOption)

  useEffect(() => {
    if (!isOpen) {
      setFormData(defaultCancellationReasonFormData)
      setIsReasonOpen(false)
    }
  }, [isOpen])

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && !isLoading && onClose()}>
      <DialogContent
        className='bg-white w-[480px] max-w-[calc(100vw-2rem)] !rounded-2xl !p-0 !gap-0 [&>button]:hidden flex flex-col'
        onEscapeKeyDown={e => {
          if (isReasonOpen) e.preventDefault()
        }}
      >
        <DialogHeader className='px-5 pt-4 pb-0'>
          <div className='flex items-start justify-between gap-4 w-full'>
            <div className='flex flex-col flex-1 min-w-0'>
              <p className='text-text-xs-medium text-zinc-500'>{subtitle}</p>
              <DialogTitle className='!text-base !font-semibold !text-zinc-800 !leading-6'>{title}</DialogTitle>
            </div>
            <IconButton
              variant='ghost'
              size='sm'
              IconComponent={X}
              aria-label='Close'
              className='-mr-1.5 -mt-1.5 text-zinc-500'
              onClick={onClose}
            />
          </div>
        </DialogHeader>

        <div className='flex-1 p-5 flex flex-col gap-4 overflow-y-auto'>
          <p className='text-text-sm-regular text-zinc-500'>{t('cancellation_reason_dialogue_text')}</p>

          <div className='flex flex-col gap-1.5'>
            <span className='text-text-xs-medium text-zinc-500'>Reason for cancelling</span>
            <Select
              open={isReasonOpen}
              onOpenChange={setIsReasonOpen}
              value={formData.selectedOption || undefined}
              onValueChange={value => setFormData(prev => ({ ...prev, selectedOption: value }))}
            >
              <SelectTrigger className='w-full !bg-white data-[placeholder]:!text-[#9F9FA9]'>
                <SelectValue placeholder='Select reason' />
              </SelectTrigger>
              <SelectContent>
                {CANCELLATION_REASON_KEYS.map(reasonKey => (
                  <SelectItem key={reasonKey} value={t(reasonKey)}>
                    {t(reasonKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='flex flex-col gap-1.5'>
            <span className='text-text-xs-medium text-zinc-500'>
              Any feedback for us? <span className='text-zinc-400'>(optional)</span>
            </span>
            <Textarea
              variant='outline'
              dimension='md'
              placeholder='Your feedback matters...'
              value={formData.detailedReason}
              onChange={e => setFormData(prev => ({ ...prev, detailedReason: e.target.value }))}
              className='placeholder:text-[#9F9FA9]'
            />
          </div>
        </div>

        <DialogFooter className='px-5 pb-4 pt-0'>
          <Button variant='outline' size='md' onClick={onClose} disabled={isLoading}>
            Keep this app
          </Button>
          <Button
            variant='outline'
            size='md'
            onClick={() => onConfirm(formData)}
            disabled={!hasSelectedReason || isLoading}
            loading={isLoading}
          >
            {isLoading ? 'Uninstalling...' : 'Uninstall'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CancellationReasonDialog
